import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useCanvaApi } from '../api';
import { resetError, setCanvaDesings, setError, setLoading, setCanvaQuery } from '../slices';

export const useCanvaService = () => {
  const dispatch = useDispatch();
  const { loading, error } = useAppSelector(state => state.canva);
  const { getState } = useStore<RootState>();

  const api = useCanvaApi();

  const getCanvaCompanyPortalLink = useCallback(async () => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getCanvaPortalLink: true }));

      return await api.getCanvaCompanyPortalLink();
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getCanvaPortalLink: false }));
    }
  }, []);

  const connectToCanva = useCallback(async (code: string, state: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ connectToCanva: true }));
      await api.connectToCanva(code, state);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ connectToCanva: false }));
    }
  }, []);

  const disconnectFromCanva = useCallback(async () => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ disconnectFromCanva: true }));

      await api.disconnectFromCanva();
      dispatch(setCanvaDesings(undefined));
      return false;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ disconnectFromCanva: false }));
    }
  }, []);

  const getDesigns = useCallback(async () => {
    const query = getState().canva.query;
    try {
      dispatch(resetError());
      dispatch(setLoading({ getDesigns: true }));

      const result = await api.getDesigns(query);
      if (result.data) {
        return result.data;
      } else {
        dispatch(setCanvaDesings(undefined));
      }
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getDesigns: false }));
    }
  }, []);

  const getDesign = useCallback(async (companyId: string, designId: string, name?: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getDesign: true }));

      const result = await api.getDesign(companyId, designId, name);
      return result;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getDesign: false }));
    }
  }, []);

  const changeCanvaQuery = useCallback((query: string | undefined) => { 
    dispatch(setCanvaQuery(query));
  }, []);

  return {
    loading,
    error,
    getCanvaCompanyPortalLink,
    connectToCanva,
    changeCanvaQuery,
    disconnectFromCanva,
    getDesigns,
    getDesign,
  };
};
